import React, { Component } from "react";
import { connect } from "react-redux";
import { Alert, Breadcrumb, Button, Card, Form, Modal } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { apiCall } from "../../utils/apiCall";
import { getToken } from "../../helpers/tokenHelper";
import * as XLSX from 'xlsx';

class PaymentTransaction extends Component {
    state = {
        loading: false,
        validated: false,
        file: null,
        errorMessage: null,
        successMessage: null,
        showModal: false,
        transactionType: 'BNumbers', // Default to BNumbers
    }

    handleSubmit = (event) => {
        event.preventDefault();
        this.setState({ loading: true, errorMessage: null });

        const form = event.currentTarget;
        if (form.checkValidity() === false || !this.state.file) {
            event.stopPropagation();
            this.setState({ loading: false, errorMessage: "Please select a valid file." });
            return;
        }

        // Validate file based on transaction type
        if (this.state.transactionType === 'TCP') {
            this.validateTcpFile(this.state.file)
                .then(() => this.uploadFile())
                .catch(error => {
                    this.setState({ 
                        errorMessage: error.message, 
                        loading: false,
                        file: null,
                        validated: false 
                    });
                });
        } else {
            // BNumbers - existing validation
            this.uploadFile();
        }
    };

    uploadFile = () => {
        const formData = new FormData();
        formData.append("file", this.state.file);
        formData.append("transactionType", this.state.transactionType);
        
        const token = getToken();
        apiCall.post(`/intelligrapi/uploaddailytransactions`, formData, {
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "multipart/form-data"
            }
        })
            .then(response => {
                const message = this.state.transactionType === 'TCP' 
                    ? "TCP file upload initiated. Processing in background."
                    : "File uploaded and processing started.";
                alert(message);
                this.setState({
                    errorMessage: null,
                    file: null,
                    validated: false,
                    loading: false,
                });
                // Reset file input
                document.getElementById("formFile").value = "";
            })
            .catch(error => {
                alert(error?.message ?? "An error occurred while uploading the file.");
                this.setState({
                    errorMessage: error?.message ?? "An error occurred while uploading the file.",
                    file: null,
                    validated: false,
                    loading: false,
                });
                // Reset file input
                document.getElementById("formFile").value = "";
            });
    };

    validateTcpFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });

                    // Check if Sheet1 exists
                    if (!workbook.SheetNames.includes('Sheet1')) {
                        reject(new Error('TCP file must contain a sheet named "Sheet1"'));
                        return;
                    }

                    // Get Sheet1
                    const sheet = workbook.Sheets['Sheet1'];
                    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                    // Check if file has data
                    if (jsonData.length === 0) {
                        reject(new Error('Sheet1 is empty'));
                        return;
                    }

                    // Validate headers
                    const expectedHeaders = ['From', 'Amount', 'TransactionId', 'ExternalId', 'Date', 'Status', 'Type'];
                    const actualHeaders = jsonData[0];

                    if (!actualHeaders || actualHeaders.length !== expectedHeaders.length) {
                        reject(new Error(`TCP file must have exactly 7 columns: ${expectedHeaders.join(', ')}`));
                        return;
                    }

                    // Check each header
                    for (let i = 0; i < expectedHeaders.length; i++) {
                        if (actualHeaders[i] !== expectedHeaders[i]) {
                            reject(new Error(`Column ${i + 1} must be named "${expectedHeaders[i]}", found "${actualHeaders[i]}"`));
                            return;
                        }
                    }

                    // Validation passed
                    resolve();
                } catch (error) {
                    reject(new Error(`Error reading file: ${error.message}`));
                }
            };

            reader.onerror = () => {
                reject(new Error('Error reading file'));
            };

            reader.readAsArrayBuffer(file);
        });
    };

    isValidExcelFile = (file) => {
        return file.name.toLowerCase().endsWith(".xlsx");
    };

    handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!this.isValidExcelFile(file)) {
            this.setState({ errorMessage: "Please upload a .xlsx file.", file: null, validated: false });
            return;
        }
        
        this.setState({ file: file, validated: true, errorMessage: null });
    };

    handleTransactionTypeChange = (event) => {
        this.setState({ 
            transactionType: event.target.value,
            file: null,
            validated: false,
            errorMessage: null
        });
        // Reset file input
        const fileInput = document.getElementById("formFile");
        if (fileInput) {
            fileInput.value = "";
        }
    };

    handleCloseModal = () => {
        this.setState({ showModal: false });
    };

    render() {
        const { loading, validated, errorMessage, csvContent, showModal, transactionType } = this.state;

        return (
            <>
                <div className="d-xl-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-xl-0">
                        <Breadcrumb className="d-none d-md-inline-block"
                            listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
                            <Breadcrumb.Item href="#"><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
                            <Breadcrumb.Item active>Upload Transaction</Breadcrumb.Item>
                        </Breadcrumb>
                        <h4>Upload Transaction</h4>
                    </div>
                </div>
                <Card border="light" className="table-wrapper shadow-sm mb-4">
                    <Card.Body>
                        <Form noValidate validated={validated} onSubmit={this.handleSubmit}>
                            {/* Transaction Type Selection */}
                            <Form.Group className="mb-3">
                                <Form.Label>Transaction Type</Form.Label>
                                <div>
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="BNumbers"
                                        name="transactionType"
                                        value="BNumbers"
                                        checked={transactionType === 'BNumbers'}
                                        onChange={this.handleTransactionTypeChange}
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="TCP"
                                        name="transactionType"
                                        value="TCP"
                                        checked={transactionType === 'TCP'}
                                        onChange={this.handleTransactionTypeChange}
                                    />
                                </div>
                            </Form.Group>

                            {/* File Upload */}
                            <div>
                                <Form.Group controlId="formFile" className="mb-3 has-error">
                                    <Form.Label>
                                        {transactionType === 'TCP' 
                                            ? 'TCP Transaction File (.xlsx with Sheet1)' 
                                            : 'Payment Transaction File (.xlsx)'}
                                    </Form.Label>
                                    <Form.Control type="file" onChange={this.handleFileChange} required />
                                    <Form.Control.Feedback type="invalid">Please choose a XLSX file.</Form.Control.Feedback>
                                    {transactionType === 'TCP' && (
                                        <Form.Text className="text-muted">
                                            File must contain Sheet1 with headers: From, Amount, TransactionId, ExternalId, Date, Status, Type
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </div>
                            
                            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                            
                            <Button variant="primary" disabled={validated !== true || loading === true} type="submit">
                                {loading ? 'Uploading ...' : 'Upload'}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
                <Modal as="div" centered show={showModal} onHide={this.handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Preview CSV</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ height: "85vh", overflowY: "auto" }}>
                        <pre>{csvContent}</pre>
                    </Modal.Body>
                </Modal>
            </>
        );
    }
}

const mapStateToProps = (store) => {
    return {
        loading: store.loading,
    };
}
export default connect(mapStateToProps, {})(PaymentTransaction);
